import { useEffect, useRef, useState } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useApi } from '../api/api-context';
import { ERROR_CODES, isApiError } from '../api/client';
import type { NodeRef, RepertoireEdge, RepertoireLine } from '../api/types';
import {
  applyMove,
  back,
  canGoBack,
  canGoForward,
  findOccurrences,
  forward,
  sameNode,
} from '../state/navigation';
import { buildNodeRoute } from '../state/route';
import { rememberBuilderRoute } from '../state/recent-builder';
import { readDraft, writeDraft } from '../state/draft';
import type { BoardInput } from '../board/chessground';
import { prepareLineSave } from '../state/line-save';
import { BoardView } from './BoardView';
import { CopyPgn } from './CopyPgn';
import { EngineStrip, EvalBar } from './LocalEval';
import { ImportPanel } from './ImportPanel';
import { GameLinkPrompt } from './GameLinkPrompt';
import { reviewRoute } from '../state/review-session';
import { MoveHistory } from './MoveHistory';
import { LineContextMenu, RenameLineDialog, type LineMenuState } from './LineRename';
import { MoveSuggestions } from './MoveSuggestions';
import { StatusBanner } from './StatusBanner';
import { TranspositionBadge } from './TranspositionBadge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { studyPgn } from '../lib/pgn';
import { isLocalAnalysisActive, useLocalAnalysis } from '../engine/useLocalAnalysis';
import {
  useLocalAnalysisPreference,
  writeLocalAnalysisPreference,
} from '../engine/preference';
import { useEngineSettings } from '../engine/settings';
import { clampLineName } from '../lib/line-name';
import {
  ChevronLeft,
  ChevronRight,
  Download,
  FlipHorizontal,
  Hammer,
  Pencil,
  SkipBack,
  SkipForward,
} from 'lucide-react';

type LineAction = 'promote' | 'demote' | 'delete';

/** A focused, always-on repertoire builder backed by the canonical DAG API. */
export function NodePage(): JSX.Element {
  const { repertoireId } = useParams<{ repertoireId: string }>();
  return <NodeStudio key={repertoireId} />;
}

function NodeStudio(): JSX.Element {
  const { repertoireId, zobristHash, positionId } = useParams<{
    repertoireId: string;
    zobristHash: string;
    positionId: string;
  }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const api = useApi();
  const queryClient = useQueryClient();

  const [orientation, setOrientation] = useState<'w' | 'b'>('w');
  const [restored] = useState(() => (repertoireId ? readDraft(repertoireId) : null));
  const [workingLine, setWorkingLine] = useState<RepertoireLine | null>(
    restored?.line ?? null,
  );
  const [draftStorageFailed, setDraftStorageFailed] = useState(false);
  const [addNotice, setAddNotice] = useState<string | null>(null);
  const [addError, setAddError] = useState<string | null>(null);
  const [addBusy, setAddBusy] = useState(false);
  const [lineActionBusy, setLineActionBusy] = useState(false);
  const [lineActionNotice, setLineActionNotice] = useState<string | null>(null);
  const [lineActionError, setLineActionError] = useState<string | null>(null);
  const [railTab, setRailTab] = useState('build');
  const [lineMenu, setLineMenu] = useState<LineMenuState | null>(null);
  const [lineToRename, setLineToRename] = useState<RepertoireLine | null>(null);
  const displayedBoard = useRef<BoardInput | null>(null);
  const displayedTransposition = useRef<{ node: NodeRef | null; incomingPathCount: number }>({
    node: null,
    incomingPathCount: 0,
  });
  const engineEnabled = useLocalAnalysisPreference();
  const engineSettings = useEngineSettings();

  useEffect(() => {
    if (!repertoireId) return;
    setDraftStorageFailed(
      !writeDraft(
        repertoireId,
        workingLine
          ? {
              version: 1,
              line: workingLine,
              name: '',
              building: true,
              role: 'alternative',
            }
          : null,
      ),
    );
  }, [repertoireId, workingLine]);

  const repertoireQuery = useQuery({
    queryKey: ['repertoire', repertoireId],
    queryFn: () => api.getRepertoire(repertoireId!),
    enabled: !!repertoireId,
  });
  const nodeQuery = useQuery({
    queryKey: ['node', repertoireId, zobristHash, positionId],
    queryFn: () => api.getNode(repertoireId!, zobristHash!, positionId!),
    enabled: !!repertoireId && !!zobristHash && !!positionId,
    placeholderData: keepPreviousData,
  });
  useEffect(() => {
    if (repertoireId && nodeQuery.data && !nodeQuery.isPlaceholderData) {
      rememberBuilderRoute(repertoireId, `${location.pathname}${location.search}`);
    }
  }, [repertoireId, nodeQuery.data, nodeQuery.isPlaceholderData, location.pathname, location.search]);
  const linesQuery = useQuery({
    queryKey: ['lines', repertoireId],
    queryFn: () => api.listLines(repertoireId!),
    enabled: !!repertoireId,
  });

  useEffect(() => {
    if (repertoireQuery.data?.data.side === 'black') setOrientation('b');
  }, [repertoireQuery.data?.data.side]);

  const node = nodeQuery.data?.data ?? null;
  const lines = linesQuery.data?.items ?? [];
  const rawLineId = searchParams.get('path');
  const rawPly = searchParams.get('ply');
  let line =
    rawLineId === 'draft'
      ? workingLine
      : rawLineId
        ? (lines.find((candidate) => candidate.lineId === rawLineId) ?? null)
        : null;
  const ply = rawPly === null ? 0 : Number.parseInt(rawPly, 10);
  let path =
    line !== null &&
    Number.isInteger(ply) &&
    ply >= 0 &&
    ply < line.nodes.length &&
    line.nodes[ply].positionId === positionId &&
    line.nodes[ply].zobristHash === zobristHash
      ? { lineId: line.lineId, ply }
      : null;

  // Adopt a saved path for a bare canonical-node URL. Identity remains the node;
  // the selected line only supplies move-order navigation.
  if (path === null && node !== null && lines.length > 0 && rawLineId !== 'draft') {
    const ref: NodeRef = {
      hashVersion: node.position.hashVersion,
      zobristHash: node.position.zobristHash,
      positionId: node.position.positionId,
    };
    const occurrence = findOccurrences(ref, lines)[0];
    if (occurrence !== undefined) {
      const adopted = lines.find((candidate) => candidate.lineId === occurrence.lineId) ?? null;
      if (adopted !== null) {
        line = adopted;
        path = { lineId: adopted.lineId, ply: occurrence.ply };
      }
    }
  }

  const currentRef: NodeRef | null =
    node === null ||
    nodeQuery.isPlaceholderData ||
    node.position.positionId !== positionId ||
    node.position.zobristHash !== zobristHash ||
    addBusy ||
    lineActionBusy
      ? null
      : {
          hashVersion: node.position.hashVersion,
          zobristHash: node.position.zobristHash,
          positionId: node.position.positionId,
        };

  function go(target: NodeRef, targetPath: { lineId: string; ply: number } | null): void {
    navigate(
      buildNodeRoute({
        repertoireId: repertoireId!,
        zobristHash: target.zobristHash,
        positionId: target.positionId,
        path: targetPath,
      }),
    );
  }

  function navigateEdge(edge: RepertoireEdge): void {
    if (currentRef === null) return;
    const next = applyMove({ node: currentRef, path }, line, edge.transition.to);
    if (next.node !== null) go(next.node, next.path);
  }

  function buildExtendedLine(
    edge: RepertoireEdge,
  ): { draft: RepertoireLine; replacedLineId: string | null } | null {
    if (!node || !repertoireQuery.data || currentRef === null) return null;
    const base = line && path ? line : null;
    // A saved line is a move order from the repertoire root. Without one reaching this
    // position (bare node URL, Back past a draft, lines still loading) the move cannot
    // start a line here: saving [currentRef, move] would replay it from the root.
    if (base === null && !sameNode(repertoireQuery.data.data.defaultRoot.position, currentRef)) {
      return null;
    }
    const prefix = base ? path!.ply : 0;
    const extendsSavedLeaf =
      base !== null && base.lineId !== 'draft' && prefix === base.movesUci.length;
    const name = extendsSavedLeaf
      ? base.name || 'Main line'
      : base?.lineId === 'draft'
        ? base.name || 'Main line'
        : base
          ? clampLineName(`${base.name || 'Line'} · ${edge.transition.san}`)
          : lines.length === 0
            ? 'Main line'
            : `${edge.transition.san} variation`;
    const draft: RepertoireLine = {
      lineId: 'draft',
      repertoireId: repertoireId!,
      rootId: base?.rootId ?? repertoireQuery.data.data.defaultRoot.rootId,
      name,
      active: true,
      createdAt: new Date().toISOString(),
      movesUci: [...(base?.movesUci.slice(0, prefix) ?? []), edge.transition.uci],
      movesSan: [...(base?.movesSan?.slice(0, prefix) ?? []), edge.transition.san],
      nodes: [...(base?.nodes.slice(0, prefix + 1) ?? [currentRef]), edge.transition.to],
      edgeIds: [...(base?.edgeIds.slice(0, prefix) ?? []), edge.edgeId],
    };
    return { draft, replacedLineId: extendsSavedLeaf ? base.lineId : null };
  }

  async function saveExtendedLine(edge: RepertoireEdge, knownEtag?: string | null): Promise<boolean> {
    if (!repertoireId) return false;
    const extension = buildExtendedLine(edge);
    if (!extension) {
      if (currentRef === null) return false;
      // The move is (or already was) in the repertoire; step onto it without a line.
      go(edge.transition.to, null);
      setAddNotice(
        `${edge.transition.san} is in your repertoire. Open a line through this position to save it in a line.`,
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['node', repertoireId] }),
        queryClient.invalidateQueries({ queryKey: ['repertoire', repertoireId] }),
      ]);
      return true;
    }
    const { draft, replacedLineId } = extension;
    setWorkingLine(draft);
    go(edge.transition.to, { lineId: 'draft', ply: draft.movesUci.length });

    try {
      const saveBody = await prepareLineSave(draft, draft.name || 'Main line', async (ref) => {
        const result = await api.getNode(repertoireId, ref.zobristHash, ref.positionId);
        return result.data;
      });
      const etag = knownEtag ?? (await api.getRepertoire(repertoireId)).etag;
      if (!etag) throw new Error('Could not load the current repertoire version.');
      const created = await api.createLine(
        repertoireId,
        crypto.randomUUID(),
        etag,
        saveBody,
      );
      let cleanupFailed = false;
      if (replacedLineId) {
        try {
          const deleteEtag = created.etag ?? (await api.getRepertoire(repertoireId)).etag;
          if (!deleteEtag) throw new Error('Could not load the current repertoire version.');
          await api.deleteLine(repertoireId, replacedLineId, deleteEtag, false);
        } catch {
          cleanupFailed = true;
        }
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['node', repertoireId] }),
        queryClient.invalidateQueries({ queryKey: ['lines', repertoireId] }),
        queryClient.invalidateQueries({ queryKey: ['repertoire', repertoireId] }),
      ]);
      const saved = created.data.line;
      const savedPly = saved.movesUci.length;
      const savedNode = saved.nodes[savedPly] ?? edge.transition.to;
      go(savedNode, { lineId: saved.lineId, ply: savedPly });
      setWorkingLine(null);
      setAddNotice(
        cleanupFailed
          ? `${edge.transition.san} saved. The previous shorter line is still listed.`
          : `${edge.transition.san} saved.`,
      );
      return true;
    } catch (error) {
      setAddError(
        isApiError(error, ERROR_CODES.VERSION_MISMATCH)
          ? 'Your repertoire changed before this line was saved. Your current path is kept.'
          : 'The move is in your repertoire, but its line could not be saved. Your current path is kept.',
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['node', repertoireId] }),
        queryClient.invalidateQueries({ queryKey: ['repertoire', repertoireId] }),
      ]);
      return true;
    }
  }

  function browseEdge(edge: RepertoireEdge): void {
    if (currentRef === null) return;
    if (line && path && line.movesUci[path.ply] === edge.transition.uci) {
      navigateEdge(edge);
      return;
    }
    for (const candidate of lines) {
      const candidatePly = candidate.nodes.findIndex(
        (candidateNode, candidateIndex) =>
          sameNode(candidateNode, currentRef) &&
          candidate.movesUci[candidateIndex] === edge.transition.uci,
      );
      if (candidatePly >= 0) {
        go(edge.transition.to, { lineId: candidate.lineId, ply: candidatePly + 1 });
        return;
      }
    }
    go(edge.transition.to, null);
  }

  async function handleMove(uci: string): Promise<boolean> {
    if (!node || addBusy || lineActionBusy || nodeQuery.isPlaceholderData) return false;
    setAddError(null);
    setLineActionError(null);
    setLineActionNotice(null);

    const edge = node.outgoing.find(
      (candidate) => candidate.enabled && candidate.transition.uci === uci,
    );
    if (edge) {
      const expected = line && path ? line.movesUci[path.ply] : undefined;
      if (expected === uci) navigateEdge(edge);
      else {
        setAddBusy(true);
        setAddNotice(null);
        try {
          return await saveExtendedLine(edge);
        } finally {
          setAddBusy(false);
        }
      }
      return true;
    }

    const ownSide = repertoireQuery.data?.data.side;
    const ourTurn = ownSide === 'both' ||
      (ownSide === 'white' && node.position.sideToMove === 'w') ||
      (ownSide === 'black' && node.position.sideToMove === 'b');
    const role = !ourTurn
      ? 'opponent'
      : node.outgoing.some((candidate) => candidate.enabled && candidate.role === 'main')
        ? 'alternative'
        : 'main';
    setAddBusy(true);
    setAddNotice(null);
    try {
      const latest = await api.getRepertoire(repertoireId!);
      if (!latest.etag) {
        throw new Error('Could not load the current repertoire version.');
      }
      const { data: added, etag } = await api.addMove(
        repertoireId!,
        latest.etag,
        crypto.randomUUID(),
        {
          fromFen: node.position.canonicalFen,
          uci,
          role,
          priority: role === 'main' ? 100 : 50,
        },
      );
      return await saveExtendedLine(added, etag);
    } catch (error) {
      setAddError(
        isApiError(error, ERROR_CODES.VERSION_MISMATCH)
          ? 'Your repertoire changed. Try the move again using the latest position.'
          : 'Could not add this move. Check the position and try again.',
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['node', repertoireId] }),
        queryClient.invalidateQueries({ queryKey: ['repertoire', repertoireId] }),
      ]);
      return false;
    } finally {
      setAddBusy(false);
    }
  }

  async function handleLineAction(action: LineAction, targetPly: number): Promise<void> {
    if (!line || !repertoireId || targetPly < 1 || targetPly >= line.nodes.length) return;
    const source = line.nodes[targetPly - 1];
    const edgeId = line.edgeIds[targetPly - 1];
    const uci = line.movesUci[targetPly - 1];
    setLineActionBusy(true);
    setLineActionError(null);
    setLineActionNotice(null);
    setAddNotice(null);
    try {
      const [{ data: sourceNode }, latest] = await Promise.all([
        api.getNode(repertoireId, source.zobristHash, source.positionId),
        api.getRepertoire(repertoireId),
      ]);
      const selected = sourceNode.outgoing.find(
        (candidate) => candidate.edgeId === edgeId || candidate.transition.uci === uci,
      );
      if (!selected || !latest.etag) throw new Error('This move is no longer available.');

      if (action === 'delete') {
        await api.disableEdge(repertoireId, selected.edgeId, latest.etag);
        const truncated: RepertoireLine = {
          ...line,
          lineId: 'draft',
          name: 'Working variation',
          movesUci: line.movesUci.slice(0, targetPly - 1),
          movesSan: line.movesSan?.slice(0, targetPly - 1),
          nodes: line.nodes.slice(0, targetPly),
          edgeIds: line.edgeIds.slice(0, targetPly - 1),
        };
        setWorkingLine(truncated);
        go(source, { lineId: 'draft', ply: targetPly - 1 });
        setLineActionNotice('Move and its continuation removed from this branch.');
      } else if (action === 'demote') {
        await api.updateEdge(repertoireId, selected.edgeId, latest.etag, {
          role: 'alternative',
          priority: 50,
        });
        setLineActionNotice(`${selected.transition.san} is now a variation.`);
      } else {
        let nextEtag = latest.etag;
        const previousMain = sourceNode.outgoing.find(
          (candidate) =>
            candidate.enabled && candidate.role === 'main' && candidate.edgeId !== selected.edgeId,
        );
        if (previousMain) {
          const demoted = await api.updateEdge(repertoireId, previousMain.edgeId, nextEtag, {
            role: 'alternative',
            priority: 50,
          });
          nextEtag = demoted.etag ?? (await api.getRepertoire(repertoireId)).etag ?? '';
        }
        if (!nextEtag) throw new Error('Could not load the latest repertoire version.');
        await api.updateEdge(repertoireId, selected.edgeId, nextEtag, {
          role: 'main',
          priority: 100,
        });
        setLineActionNotice(`${selected.transition.san} is now the main line.`);
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['node', repertoireId] }),
        queryClient.invalidateQueries({ queryKey: ['lines', repertoireId] }),
        queryClient.invalidateQueries({ queryKey: ['repertoire', repertoireId] }),
      ]);
    } catch (error) {
      setLineActionError(
        isApiError(error, ERROR_CODES.VERSION_MISMATCH)
          ? 'Your repertoire changed before this edit completed. Try again.'
          : error instanceof Error
            ? error.message
            : 'Could not update this line.',
      );
    } finally {
      setLineActionBusy(false);
    }
  }

  function handleBack(): void {
    if (currentRef === null || !canGoBack({ node: currentRef, path })) return;
    const next = back({ node: currentRef, path }, line);
    if (next.node !== null && next.path !== null) go(next.node, next.path);
  }

  function handleForward(): void {
    if (currentRef === null || line === null || !canGoForward({ node: currentRef, path }, line)) {
      return;
    }
    const next = forward({ node: currentRef, path }, line);
    if (next.node !== null && next.path !== null) go(next.node, next.path);
  }

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent): void {
      if (event.defaultPrevented || document.querySelector('[role=dialog], [role=menu]')) return;
      // Leave browser and OS shortcuts alone (Ctrl/Cmd+F find, Alt+Arrow history).
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.closest('button, [role=tab], summary, [contenteditable=true]') ||
          ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))
      ) {
        return;
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        handleBack();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        handleForward();
      } else if (event.key.toLowerCase() === 'f') {
        event.preventDefault();
        setOrientation((value) => (value === 'w' ? 'b' : 'w'));
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  });

  const sideToMove = node?.position.sideToMove;
  const statusText =
    sideToMove === 'w' ? 'White to move' : sideToMove === 'b' ? 'Black to move' : 'Position';
  const boardReady = node !== null &&
    !nodeQuery.isPlaceholderData &&
    node.position.positionId === positionId &&
    node.position.zobristHash === zobristHash &&
    !addBusy &&
    !lineActionBusy;
  if (boardReady) {
    displayedBoard.current = {
          fen: node.position.canonicalFen,
          turnColor: node.position.sideToMove,
          orientation,
          dests: node.legalMoves,
          lastMove:
            line !== null && path !== null && path.ply > 0
              ? line.movesUci[path.ply - 1]
              : undefined,
    };
    // The transposition chip describes the displayed board, so it holds with it
    // while the next node loads instead of flashing off and on.
    displayedTransposition.current = {
      node: currentRef,
      incomingPathCount: node.incomingPathCount,
    };
  }
  const boardInput = displayedBoard.current;
  // Local, advisory analysis of the shown position (ADR-0015): it follows the
  // displayed board, so a save in flight keeps the last position's bar steady.
  const analysis = useLocalAnalysis(boardInput?.fen ?? null, engineEnabled, {
    depth: engineSettings.depth,
    multiPv: engineSettings.lines,
  });
  const analysedSide = boardInput?.turnColor ?? 'w';

  function toggleEngine(next: boolean): void {
    writeLocalAnalysisPreference(next);
  }

  async function getPgn(): Promise<string> {
    if (!node) throw new Error('No position loaded');
    const name = line?.name ?? repertoireQuery.data?.data.name ?? 'ItsAllPrep study';
    if (line && path && line.movesSan?.length === line.movesUci.length) {
      const root = line.nodes[0];
      const { data } = await api.getNode(repertoireId!, root.zobristHash, root.positionId);
      return studyPgn(data.position.canonicalFen, name, line.movesSan.slice(0, path.ply));
    }
    return studyPgn(node.position.canonicalFen, name);
  }

  if (nodeQuery.isError) {
    return (
      <main className="page">
        <p role="alert">Failed to load this position.</p>
        <Link to="/">← All repertoires</Link>
      </main>
    );
  }

  return (
    <main className="analysis-layout focused-builder">
      <section className="analysis-center" aria-label="Board">
        <header className="studio-context">
          <p className="studio-eyebrow">REPERTOIRE BUILDER</p>
          <h1>{repertoireQuery.data?.data.name ?? 'Repertoire'}</h1>
          <Link className="builder-back-link" to={`/repertoires/${repertoireId}`}>
            ← Repertoire
          </Link>
          {repertoireId && currentRef && (
            <Link
              className="builder-practice-link"
              to={reviewRoute(repertoireId, {
                scope: { zobristHash: currentRef.zobristHash, positionId: currentRef.positionId },
              })}
            >
              Practice from here
            </Link>
          )}
          <div className="studio-status-row flex items-center justify-end gap-3 text-sm">
            <p className="status" data-testid="status-text">
              {statusText}
            </p>
            <TranspositionBadge
              key={positionId}
              node={displayedTransposition.current.node}
              lines={lines}
              currentPath={path}
              incomingPathCount={displayedTransposition.current.incomingPathCount}
              onNavigate={(targetLineId, targetPly) => {
                if (currentRef === null) return;
                const target = lines.find((candidate) => candidate.lineId === targetLineId)
                  ?.nodes[targetPly];
                if (target) go(target, { lineId: targetLineId, ply: targetPly });
              }}
            />
          </div>
        </header>
        {boardInput !== null ? (
          <div className="board-with-eval">
            {isLocalAnalysisActive(analysis) && (
              <EvalBar analysis={analysis} sideToMove={analysedSide} orientation={orientation} />
            )}
            <BoardView
              input={boardInput}
              onMove={handleMove}
              label={`Chessboard, ${statusText.toLowerCase()}`}
              busy={!boardReady}
            />
          </div>
        ) : (
          <p role="status">Loading board…</p>
        )}
        <nav className="board-controls flex items-center justify-between" aria-label="Path navigation">
          <button
            type="button"
            aria-label="Flip board"
            title="Flip board (F)"
            onClick={() => setOrientation((value) => (value === 'w' ? 'b' : 'w'))}
          >
            <FlipHorizontal size={18} />
          </button>
          <div className="board-navigation">
            <button
              type="button"
              aria-label="First move"
              disabled={!canGoBack({ node: currentRef, path })}
              onClick={() => {
                const target = line?.nodes[0];
                if (target && line) go(target, { lineId: line.lineId, ply: 0 });
              }}
            >
              <SkipBack size={18} />
            </button>
            <button
              type="button"
              aria-label="Back"
              onClick={handleBack}
              disabled={!canGoBack({ node: currentRef, path })}
            >
              <ChevronLeft size={20} />
            </button>
            <button
              type="button"
              aria-label="Forward"
              onClick={handleForward}
              disabled={!canGoForward({ node: currentRef, path }, line)}
            >
              <ChevronRight size={20} />
            </button>
            <button
              type="button"
              aria-label="Last move"
              disabled={!canGoForward({ node: currentRef, path }, line)}
              onClick={() => {
                const targetPly = (line?.nodes.length ?? 1) - 1;
                const target = line?.nodes[targetPly];
                if (target && line) go(target, { lineId: line.lineId, ply: targetPly });
              }}
            >
              <SkipForward size={18} />
            </button>
          </div>
          <CopyPgn
            key={`${positionId}-${path?.lineId}`}
            getPgn={getPgn}
            disabled={!node || nodeQuery.isPlaceholderData}
          />
        </nav>
        <EngineStrip
          analysis={analysis}
          fen={boardInput?.fen ?? null}
          sideToMove={analysedSide}
          enabled={engineEnabled}
          onToggle={toggleEngine}
        />
      </section>

      <aside className="analysis-right builder-rail" aria-label="Repertoire builder">
        <Tabs value={railTab} onValueChange={setRailTab}>
          <TabsList aria-label="Repertoire tools">
            <TabsTrigger value="build">
              <Hammer size={16} />
              Build
            </TabsTrigger>
            <TabsTrigger value="import">
              <Download size={16} />
              Import
            </TabsTrigger>
          </TabsList>
          <TabsContent value="build">
            {node && repertoireQuery.data && (
              <MoveSuggestions
                position={node.position}
                legalMoves={node.legalMoves}
                outgoing={node.outgoing}
                lines={lines}
                side={repertoireQuery.data.data.side}
                onMove={handleMove}
                busy={addBusy || lineActionBusy || nodeQuery.isPlaceholderData}
              />
            )}
            <GameLinkPrompt compact />
            <StatusBanner node={node} hideTransposition />
            {(addBusy ||
              lineActionBusy ||
              (draftStorageFailed && workingLine) ||
              addNotice ||
              lineActionNotice ||
              addError ||
              lineActionError) && (
              <section className="line-builder" aria-label="Build status">
                {addBusy || lineActionBusy ? <span className="saving-dot">Saving…</span> : null}
                {draftStorageFailed && workingLine && (
                  <p role="alert">
                    This browser could not remember the current path after a refresh.
                  </p>
                )}
                {addNotice && (
                  <p role="status" className="notice">
                    {addNotice}
                  </p>
                )}
                {lineActionNotice && (
                  <p role="status" className="notice">
                    {lineActionNotice}
                  </p>
                )}
                {(addError || lineActionError) && (
                  <p role="alert" className="error">
                    {addError || lineActionError}
                  </p>
                )}
              </section>
            )}

            <section className="line-browser" aria-labelledby="saved-lines-title">
              <h2 id="saved-lines-title">Lines</h2>
              {lines.length === 0 ? (
                <p className="muted">No saved lines yet.</p>
              ) : (
                <ul>
                  {lines.map((saved) => {
                    const first = saved.nodes[0];
                    const selected = line?.lineId === saved.lineId;
                    return (
                      <li
                        key={saved.lineId}
                        className="builder-line-row"
                        onContextMenu={(event) => {
                          event.preventDefault();
                          setLineMenu({
                            line: saved,
                            x: Math.max(8, Math.min(event.clientX, window.innerWidth - 220)),
                            y: Math.max(8, Math.min(event.clientY, window.innerHeight - 70)),
                          });
                        }}
                      >
                        <button
                          type="button"
                          className={`builder-line-open${selected ? ' current' : ''}`}
                          aria-current={selected ? 'true' : undefined}
                          disabled={!first || addBusy || lineActionBusy}
                          onKeyDown={(event) => {
                            if (event.key !== 'ContextMenu' && !(event.shiftKey && event.key === 'F10')) return;
                            event.preventDefault();
                            const bounds = event.currentTarget.getBoundingClientRect();
                            setLineMenu({ line: saved, x: bounds.left, y: bounds.bottom });
                          }}
                          onClick={() => {
                            if (first) go(first, { lineId: saved.lineId, ply: 0 });
                          }}
                        >
                          <span>{saved.name || 'Unnamed line'}</span>
                          <small>{saved.movesSan?.slice(0, 6).join(' ') || 'Start position'}</small>
                        </button>
                        <button
                          type="button"
                          className="builder-line-rename"
                          aria-label={`Rename ${saved.name || 'Unnamed line'}`}
                          title="Rename line"
                          onClick={() => setLineToRename(saved)}
                        >
                          <Pencil size={15} />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
            {lineMenu && (
              <LineContextMenu
                menu={lineMenu}
                onClose={() => setLineMenu(null)}
                onRename={(target) => { setLineMenu(null); setLineToRename(target); }}
              />
            )}
            {lineToRename && repertoireId && (
              <RenameLineDialog
                line={lineToRename}
                repertoireId={repertoireId}
                onClose={() => setLineToRename(null)}
                onRenamed={(name) => setLineActionNotice(`${name} renamed.`)}
              />
            )}

            <section className="continuation-browser" aria-labelledby="continuations-title">
              <h2 id="continuations-title">Continuations</h2>
              {node?.outgoing.some((edge) => edge.enabled) ? (
                <ul>
                  {node.outgoing
                    .filter((edge) => edge.enabled)
                    .map((edge) => (
                      <li key={edge.edgeId}>
                        <button
                          type="button"
                          disabled={addBusy || lineActionBusy}
                          onClick={() => browseEdge(edge)}
                          aria-label={`Browse ${edge.transition.san} ${edge.role === 'main' ? 'main line' : 'variation'}`}
                        >
                          <span>{edge.transition.san}</span>
                          <small>{edge.role === 'main' ? 'Main' : 'Variation'}</small>
                        </button>
                      </li>
                    ))}
                </ul>
              ) : (
                <p className="muted">No continuations from this position.</p>
              )}
            </section>

            <MoveHistory
              line={line}
              currentPly={path?.ply ?? 0}
              actionBusy={lineActionBusy}
              onLineAction={(action, targetPly) => void handleLineAction(action, targetPly)}
              onSelect={(targetPly) => {
                const target = line?.nodes[targetPly];
                if (target && line) go(target, { lineId: line.lineId, ply: targetPly });
              }}
            />
            {line && line.movesUci.length > 0 && (
              <p className="context-hint">Right-click any move to promote, demote, or delete from there.</p>
            )}
          </TabsContent>
          {/* Kept mounted while hidden: the import finishes saving its lines after the
              parse job succeeds, so switching tabs mid-import must not unmount it. */}
          <TabsContent value="import" forceMount hidden={railTab !== 'import'}>
            <ImportPanel repertoireId={repertoireId!} />
          </TabsContent>
        </Tabs>
      </aside>
    </main>
  );
}
